/** @jsxImportSource @emotion/react */
import React from "react";
import {betterMemo} from "js/common/utils/more-memo";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import * as KpiRepo from "js/common/repo/backbone/kpi-repo";
import * as Formatter from "js/common/utils/formatter";
import UserAvatar from "js/common/views/user-avatar";
import {currentQueryString} from "js/common/utils/query-strings";
import {leaderBoardRankColors} from "js/dashboards/components/leaderboard/leaderboard-row";
import * as TopPerformers from "js/dashboards/components/leaderboard/top-performers";

// Heights, in unscaled px, of the pieces the panel stacks vertically, split into text, which follows
// the dashboard zoom, and everything else, which gives up or takes the room so the panel still fills
// its height. Metric text allows for a title that wraps onto two lines.
const BASE_FIXED_HEIGHT = 168;
const BASE_TEXT_HEIGHT = 72;
const METRIC_FIXED_HEIGHT = 30;
const METRIC_TEXT_HEIGHT = 75;
// The crown and photo, less the parts of each that overlap, within BASE_FIXED_HEIGHT.
const PHOTO_HEIGHT = 110;
const MIN_FIXED_SCALE = 0.2;
const MIN_TEXT_SCALE = 0.3;
const MAX_FIT_ATTEMPTS = 4;

const CrownIcon = ({colour, size}) => (
    <svg width={size} height={size * 0.8} viewBox="0 0 40 32" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 12 L12 20 L20 8 L28 20 L36 12 L33 28 H7 Z" fill={colour} />
      <circle cx="4" cy="10" r="3.5" fill={colour} />
      <circle cx="20" cy="5" r="4" fill={colour} />
      <circle cx="36" cy="10" r="3.5" fill={colour} />
    </svg>
);

const formatMetricValue = (metric, valuesByUserId, userId) => {
  const total = valuesByUserId && valuesByUserId.get(userId);
  if (!total) {
    return "--";
  }
  const valueFormat = KpiRepo.get(metric.get("kpiId")).get("valueFormat");
  return Formatter.format(total.toJS(), {valueFormat, maxDisplayLength: 10});
};

// Stays at full size on one line, and only drops to the smaller size when it has to wrap.
const MetricTitle = ({text, fontSize, wrappedFontSize, panelWidth}) => {
  const titleRef = React.useRef(null);
  const oneLineRef = React.useRef(null);
  const [wraps, setWraps] = React.useState(false);

  React.useLayoutEffect(() => {
    const fitsOnOneLine = oneLineRef.current.offsetWidth <= titleRef.current.clientWidth;
    if (fitsOnOneLine === wraps) {
      setWraps(!fitsOnOneLine);
    }
  }, [text, fontSize, panelWidth, wraps]);

  return (
      <div
          ref={titleRef}
          title={text}
          data-test-id="top-performer-metric-title"
          style={{
            position: "relative",
            fontSize: wraps ? wrappedFontSize : fontSize,
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflowWrap: "anywhere"
          }}>
        <span
            ref={oneLineRef}
            aria-hidden="true"
            style={{position: "absolute", visibility: "hidden", whiteSpace: "nowrap", fontSize}}>
          {text}
        </span>
        {text}
      </div>
  );
};

const TopPerformerPanel = betterMemo({displayName: "TopPerformerPanel"}, ({
  config,
  rank,
  user,
  value,
  metricValues,
  height,
  width,
  zoom = 1
}) => {
  const {theme} = React.useContext(CustomThemeContext);
  const metrics = TopPerformers.getMetrics(config);
  const fixedHeight = BASE_FIXED_HEIGHT + metrics.size * METRIC_FIXED_HEIGHT;
  const textHeight = BASE_TEXT_HEIGHT + metrics.size * METRIC_TEXT_HEIGHT;
  // `height` is in the dashboard's zoomed CSS pixels. The text takes the size it would have at 100%
  // in this space, so the zoom scales it just as it does the leaderboard rows, and only the photo and
  // spacing stretch or shrink to fill the height. It is capped so they always keep a minimum size.
  // The minimum is an on-screen size, so it must not grow with the zoom and squeeze the text back down.
  const minFixedScale = MIN_FIXED_SCALE / Math.max(zoom, 1);
  const textScale = Math.max(
      Math.min(height * zoom / (fixedHeight + textHeight), (height - fixedHeight * minFixedScale) / textHeight),
      MIN_TEXT_SCALE);
  const estimatedScale = (height - textHeight * textScale) / fixedHeight;
  // The estimate cannot know which titles wrap or how the fonts render, so the panel measures what it
  // actually drew and corrects the photo and spacing until it fills its height.
  const [fitCorrection, setFitCorrection] = React.useState(1);
  const fitAttemptsRef = React.useRef(0);
  const rootRef = React.useRef(null);
  const contentRef = React.useRef(null);
  const scale = Math.max(estimatedScale * fitCorrection, minFixedScale);
  // Zooming out frees up height, but a bigger photo is not what anyone zoomed out for, so it stays at
  // its default size and the spacing takes up the room instead.
  const photoScale = zoom < 1 ? Math.min(scale, height / (fixedHeight + textHeight)) : scale;
  const rankColour = leaderBoardRankColors[rank];
  const avatarSize = Math.round(100 * photoScale);
  const crownSize = Math.round(44 * photoScale);
  const cardRadius = 10;
  const userId = user.get("id");
  // The avatar is large here, so only scheduled reports trade its sharpness for a smaller download.
  const isScheduledReportMode = currentQueryString.scheduledreport?.toLowerCase() === "true";
  const displayNames = metrics.map(TopPerformers.getDisplayName).join("|");

  React.useLayoutEffect(() => {
    fitAttemptsRef.current = 0;
  }, [height, width, zoom, userId, value, displayNames]);

  React.useLayoutEffect(() => {
    const available = rootRef.current.clientHeight;
    const used = contentRef.current.offsetHeight;
    const fixedPart = (fixedHeight - (photoScale < scale ? PHOTO_HEIGHT : 0)) * scale;
    if (used > 0 && Math.abs(available - used) > 2 && fitAttemptsRef.current < MAX_FIT_ATTEMPTS) {
      fitAttemptsRef.current++;
      setFitCorrection(correction => correction * Math.max(fixedPart + available - used, 1) / fixedPart);
    }
  }, [height, width, zoom, userId, value, displayNames, metricValues, fixedHeight, scale, photoScale]);

  return (
      <div
          ref={rootRef}
          data-test-id="top-performer-panel"
          style={{
            height: "100%",
            padding: "0 15px",
            overflow: "hidden",
            color: theme.palette.text.main
          }}>
        <div ref={contentRef} style={{display: "flex", flexDirection: "column"}}>
        <div style={{fontSize: Math.round(16 * textScale), marginBottom: 8 * scale}}>
          {TopPerformers.getTitle(config)}
        </div>

        <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center"
            }}>
          {/* Keeps the avatar from jumping when the crown disappears below third place. The crown
              overlaps the top of the photo to leave more room for the metrics. */}
          <div style={{position: "relative", zIndex: 2, height: crownSize * 0.8, marginBottom: -crownSize * 0.3}}>
            {rankColour && <CrownIcon colour={rankColour} size={crownSize} />}
          </div>
          <UserAvatar
              user={user}
              eagerLoading={true}
              useThumbnail={isScheduledReportMode}
              style={{
                width: avatarSize,
                height: avatarSize,
                borderRadius: "50%",
                objectFit: "cover",
                objectPosition: "center",
                display: "block",
                border: rankColour ? `3px solid ${rankColour}` : "none",
                backgroundColor: theme.palette.background.paper,
                position: "relative",
                zIndex: 1,
                marginBottom: -12 * photoScale
              }} />
        </div>

        <div
            data-test-id="top-performer-summary"
            style={{
              backgroundColor: theme.palette.background.paper,
              borderRadius: cardRadius,
              textAlign: "center",
              padding: `${14 * scale}px 10px ${10 * scale}px`,
              lineHeight: 1.2
            }}>
          <div
              style={{
                fontSize: Math.round(24 * textScale),
                fontWeight: "bold",
                color: rankColour || theme.palette.text.main
              }}>
            {value}
          </div>
          <div
              style={{
                fontSize: Math.round(20 * textScale),
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}>
            {user.get("fullName")}
          </div>
        </div>

        {metrics.map((metric, index) => (
            <div
                key={index}
                data-test-id="top-performer-metric"
                style={{
                  marginTop: 14 * scale,
                  background: theme.palette.leaderboard.metricBackground,
                  color: theme.palette.leaderboard.metricText,
                  borderRadius: cardRadius,
                  textAlign: "center",
                  padding: `${8 * scale}px 10px`,
                  lineHeight: 1.2
                }}>
              <MetricTitle
                  text={TopPerformers.getDisplayName(metric)}
                  fontSize={Math.round(18 * textScale)}
                  wrappedFontSize={Math.round(15 * textScale)}
                  panelWidth={width} />
              <div data-test-id="top-performer-metric-value" style={{fontSize: Math.round(32 * textScale), fontWeight: "bold"}}>
                {metricValues[index] === undefined
                    ? <i className="fa fa-spinner fa-pulse" data-test-id="top-performer-metric-loading" />
                    : formatMetricValue(metric, metricValues[index], userId)}
              </div>
            </div>
        ))}
        </div>
      </div>
  );
});

export default TopPerformerPanel;
